import IconDonFilled from "@/assets/icons/don-filled.svg?react";
import CommonBadge from "@/components/common/badge/CommonBadge.tsx";
import IconChevron from "@/assets/icons/chevron.svg?react";
import { useNavigate } from "react-router";
import { useCallback, useMemo } from "react";
import dayjs from "dayjs";
import { ChallengeStatus } from "../../types";
import { calculateProgressPercentage, cn, formatNumber } from "@/shared/utils";
import { useTranslation } from "react-i18next";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import Image from "@/components/base/Image";
import { ImageType } from "../../types/common";

interface ChallengeCardProps {
  id: string;
  srcImg: ImageType;
  title: string;
  targetAmount: string;
  gainedAmount: string;
  participantFee: string;
  isNew: boolean;
  opportunityFee: string;
  deadline: string;
  status: ChallengeStatus;
}

export const LiveBadge = () => {
  return (
    <CommonBadge
      text="Live"
      color="red"
      prefix={
        <div className="w-1 h-1 rounded-full bg-white mr-1 animate-pulse" />
      }
    />
  );
};

const SoonBadge = () => {
  const { t } = useTranslation();
  return (
    <CommonBadge
      text={t("challenge.coming_soon")}
      color="orange-100"
      className="text-[#101017] "
    />
  );
};

const NewBadge = () => {
  return (
    <CommonBadge
      text="New"
      color="green"
      prefix={
        <div className="w-1 h-1 rounded-full bg-white mr-1 animate-pulse" />
      }
    />
  );
};

const ChallengeStatusBadge: React.FC<{
  status: ChallengeStatus;
  isNew: boolean;
}> = ({ status, isNew }) => {
  if (status === ChallengeStatus.DETERMINING_WINNERS) {
    return <LiveBadge />;
  }
  if (status === ChallengeStatus.COMING_SOON) {
    return <SoonBadge />;
  }

  if (isNew) {
    return <NewBadge />;
  }
  return null;
};

const getSubtitleClassName = (status: ChallengeStatus) => {
  switch (status) {
    case ChallengeStatus.COMPLETED:
    case ChallengeStatus.DETERMINING_WINNERS:
      return "text-green ";
    case ChallengeStatus.STARTED:
      return "text-yellow";
    case ChallengeStatus.COMING_SOON:
      return "text-[#101017]";
    default:
      return "text-gray-1";
  }
};

const getProgressBarColor = (status: ChallengeStatus) => {
  switch (status) {
    case ChallengeStatus.COMPLETED:
    case ChallengeStatus.STARTED:
    case ChallengeStatus.DETERMINING_WINNERS:
      return "bg-green";
    case ChallengeStatus.COMING_SOON:
      return "!bg-orange-100";
    case ChallengeStatus.ACTIVE:
      return "bg-blue";
    default:
      return "";
  }
};

const formatDeadline = (deadline: string) => {
  if (!deadline) return null;
  return dayjs(deadline).format("DD.MM.YYYY");
};

const ChallengeDefaultCard = (props: ChallengeCardProps) => {
  const {
    title,
    status,
    deadline,
    targetAmount,
    participantFee,
    gainedAmount,
    srcImg,
    isNew,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const getSubtitleText = useCallback(
    (status: ChallengeStatus, formattedDeadline: string | null) => {
      switch (status) {
        case ChallengeStatus.ACTIVE:
          return formattedDeadline;
        case ChallengeStatus.COMPLETED:
          return t("challenge.completed");
        case ChallengeStatus.STARTED:
          return t("challenge.started");
        case ChallengeStatus.DETERMINING_WINNERS:
          return t("challenge.winner_determining");
        case ChallengeStatus.CANCELED:
          return t("challenge.cancelled");
        case ChallengeStatus.COMING_SOON:
          return null;
        default:
          return formattedDeadline;
      }
    },
    [t]
  );

  const progressBar = useMemo(
    () => calculateProgressPercentage(targetAmount, gainedAmount),
    [targetAmount, gainedAmount]
  );

  const formattedDeadline = useMemo(() => formatDeadline(deadline), [deadline]);

  const subtitleText = useMemo(
    () => getSubtitleText(status, formattedDeadline),
    [formattedDeadline, getSubtitleText, status]
  );

  return (
    <VibratingButton
      onClick={() => navigate(`/challenges/${props.id}`)}
      className="border-b border-b-white/08 rounded-lg flex items-start gap-2 relative bg-white/08 p-2 transition-all click-button"
    >
      {/* Challenge Image Container */}
      <div className="relative w-[90px] h-[58px] border border-white/08 rounded-md shrink-0">
        <Image
          urls={srcImg}
          size="medium"
          alt={title}
          fallback="images/challenge-fallback.png"
          wrapperClassName="rounded-[5px]"
        />
        {[ChallengeStatus.ACTIVE].includes(status) && (
          <div className="flex z-10 items-center gap-1 p-1 pr-1.5 rounded-full bg-yellow-linear bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 absolute whitespace-nowrap">
            <IconDonFilled
              width="14"
              height="14"
              className="text-black shrink-0"
            />
            <p className="font-bold text-black text-small">
              {formatNumber(participantFee)}
            </p>
          </div>
        )}
      </div>

      {/* Challenge Information */}
      <div className="w-full">
        <div
          className={cn(status === "COMING_SOON" ? "mb-0" : "mb-2.5", "flex relative items-start justify-between gap-1")}
        >
          <div>
            <h2 className="text-left font-semibold text-base text-white leading-130 line-clamp-2">
              {title}
            </h2>
            <p
              className={cn(status === "COMING_SOON" ? "!text-gray-1 hidden" : "",
                "text-left text-xs font-normal leading-130",
                getSubtitleClassName(status)
              )}
            >
              {subtitleText}
            </p>
          </div>
          <div
            className={` ${
              status === "COMING_SOON" ? "flex flex-col !items-end gap-4" : ""
            } mt-1 flex justify-between  items-center`}
          >
            <div className={` ${status === "COMING_SOON" ? "order-2 bg-orange-100 rounded-full" : ""}`}>
              <ChallengeStatusBadge status={status} isNew={isNew} />
            </div>
            <IconChevron
              width={20}
              height={20}
              className={`shrink-0 text-gray-1`}
            />
          </div>
        </div>

        {/* Progress Bar */}
        <div
          className={` ${
            status === "COMING_SOON" ? "hidden" : ""
          } h-1 rounded-lg transition-all duration-1000 bg-white/08`}
        >
          <div
            className={`${getProgressBarColor(status)} rounded-lg h-full`}
            style={{ width: `${progressBar}%` }}
          ></div>
        </div>
      </div>
    </VibratingButton>
  );
};

export default ChallengeDefaultCard;
